import {connect} from 'react-redux';

import {
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../../../srcCommon/helpers/reselectHelpers';
import {ResolveDispatchProps} from '../../../../../../helpers/reduxHelpers';
import {ConversationBaseModel} from '../../../../../../models/api/conversationModel';
import {AppState} from '../../../../../../store/appState';
import {
  addAndRemoveTagsFromConversationsAction,
  bulkAddAndRemoveTagsFromConversationsAction,
} from '../../../../../../store/data/conversations/tagConversationsActions';
import {CellsContext} from '../../../../../../store/panels/cells/context/cellsContext';
import {InboxesParameters} from '../../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {ViewerPanelId} from '../../../../../../store/panels/panelStateBase';
import {
  makeGetAreAllStarred,
  makeGetStarredTagId,
} from '../../../../../../store/panels/viewer/conversations/viewerPanelConversationsSelectors';
import {ConversationsStarCoordinator} from './conversationsStarCoordinator';

/*
 * Props.
 */

interface ConversationsStarCoordinatorContainerProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  inboxesParameters: InboxesParameters;
  /** Potentially partial set of currently selected conversation. */
  conversations: ReadonlyArray<ConversationBaseModel>;
  isBulkActionRequired: boolean;
}

/*
 * Selectors.
 */

/** Extract the cells context from the props. */
function getCellsContext(_state: AppState, props: ConversationsStarCoordinatorContainerProps) {
  return props.cellsContext;
}

function getConversations(_state: unknown, props: ConversationsStarCoordinatorContainerProps) {
  return props.conversations;
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ConversationsStarCoordinatorContainerProps>()(() => ({
  conversations: getConversations,
  areAllStarred: makeGetAreAllStarred(getCellsContext, getConversations),
  starredTagId: makeGetStarredTagId(getCellsContext),
}));

const mapDispatchToProps = makeMapDispatchToProps<ConversationsStarCoordinatorContainerProps>()((props) => ({
  addAndRemoveTagsFromConversations: addAndRemoveTagsFromConversationsAction,
  bulkAddAndRemoveTagsFromConversations: bulkAddAndRemoveTagsFromConversationsAction,
}));

function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  dispatchProps: ResolveDispatchProps<typeof mapDispatchToProps>,
  ownProps: ConversationsStarCoordinatorContainerProps,
) {
  const {cellsContext, inboxesParameters} = ownProps;
  const {starredTagId, conversations} = stateProps;

  const conversationIds = conversations.map((c) => c.id);

  const onStar = () => {
    if (!starredTagId) {
      return;
    }

    dispatchProps.addAndRemoveTagsFromConversations(
      conversationIds,
      {
        add: [starredTagId],
      },
      true,
    );
  };

  const onUnstar = () => {
    if (!starredTagId) {
      return;
    }

    dispatchProps.addAndRemoveTagsFromConversations(
      conversationIds,
      {
        remove: [starredTagId],
      },
      true,
    );
  };

  const onBulkStar = () => {
    if (!starredTagId) {
      return;
    }

    dispatchProps.bulkAddAndRemoveTagsFromConversations(inboxesParameters, cellsContext, {
      add: [starredTagId],
    });
  };

  const onBulkUnstar = () => {
    if (!starredTagId) {
      return;
    }

    dispatchProps.bulkAddAndRemoveTagsFromConversations(inboxesParameters, cellsContext, {
      remove: [starredTagId],
    });
  };

  return {
    ...ownProps,
    ...stateProps,
    onStar,
    onUnstar,
    onBulkStar,
    onBulkUnstar,
  };
}

export const ConversationsStarCoordinatorContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(ConversationsStarCoordinator);
